import { Card, CardContent } from '@/components/ui/card';
import { BulkInviteForm, RegistrationLinkForm } from '@/components/events/EventInviteForms';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEmployeeContext } from '@/hooks/useEmployeeContext';

/**
 * Employee’s events surface — for day-to-day group invites (vendor meetings,
 * client visits with a delegation, small workshops). Same shared forms as
 * the resident and tenant-admin pages; copy is dialled for an employee
 * hosting from inside their company.
 *
 * Server-side, the employee role (50) outranks the bulk endpoint’s
 * `@Roles('resident')` minimum (40), so employees can post directly. The
 * resolver picks up `unit_id` from their employee role binding so guests
 * are correctly tagged to the tenant company at the gate.
 */
export function EmployeeEventsPage() {
  const ctx = useEmployeeContext();

  if (!ctx) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl">Events</h1>
        <p className="text-sm text-muted-foreground">
          We couldn&apos;t determine your company. Please open this page from your home dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl">Hosting a group visit</h1>
        <p className="text-sm text-muted-foreground">
          Bringing visitors in for a meeting or workshop? Pre-authorise the whole group at once.
        </p>
      </div>

      <Card>
        <CardContent className="pt-5">
          <Tabs defaultValue="link">
            <TabsList className="mb-4">
              <TabsTrigger value="link">Get a signup link</TabsTrigger>
              <TabsTrigger value="bulk" className="text-muted-foreground">
                I already have the attendee list
              </TabsTrigger>
            </TabsList>
            <TabsContent value="link">
              <p className="text-xs text-muted-foreground mb-4">
                Don’t have the full list? Share a link — attendees fill in their own details and
                receive their own gate code. Easiest option.
              </p>
              <RegistrationLinkForm
                propertyId={ctx.propertyId}
                titlePlaceholder="Workshop — Q3 product training"
              />
            </TabsContent>
            <TabsContent value="bulk">
              <p className="text-xs text-muted-foreground mb-4">
                Add each attendee’s name, phone, and any other details — they’ll each get their own
                QR code valid for the day.
              </p>
              <BulkInviteForm
                propertyId={ctx.propertyId}
                eventNamePlaceholder="Vendor demo — Acme Co"
                visitorListLabel="Attendee list"
                visitorRowNoun="Attendee"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
